import { deviceMemory, hasFinePointer, isMobileViewport, prefersReducedMotion, queryFlag, saveData } from '../motion-env';

export type NightDriveTier = 'high' | 'mid' | 'low';

/**
 * `?force3d=1` on the landing URL forces the WebGL path (tests run on SwiftShader). Read when
 * the app boots and kept for the page's lifetime, because in-app navigation drops the query.
 */
const forcedAtBoot = queryFlag('force3d');
const probes: { forced?: boolean; normal?: boolean } = {};

export function force3D(): boolean {
  return forcedAtBoot || queryFlag('force3d');
}

/** True when the Night Drive scene may run: motion allowed, a capable device and WebGL2. */
export function canRun3D(): boolean {
  if (typeof window === 'undefined' || prefersReducedMotion()) return false;
  const forced = force3D();
  if (!forced) {
    const memory = deviceMemory();
    if (saveData()) return false;
    if (memory !== undefined && memory < 4) return false;
    if (!hasFinePointer() && (navigator.hardwareConcurrency ?? 8) < 4) return false;
  }
  return probeWebGL2(forced);
}

/** Probes once per page (per flag value); the probe context is released right away. */
function probeWebGL2(forced: boolean): boolean {
  const key = forced ? 'forced' : 'normal';
  const cached = probes[key];
  if (cached !== undefined) return cached;
  let ok = false;
  try {
    const gl = document.createElement('canvas').getContext('webgl2', { failIfMajorPerformanceCaveat: !forced });
    ok = !!gl && (forced || !isSoftwareRenderer(gl));
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    ok = false;
  }
  probes[key] = ok;
  return ok;
}

const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software|basic render/i;

/**
 * CPU rasterisers (SwiftShader, llvmpipe, Microsoft Basic Render) do not always raise the
 * performance caveat, e.g. when the GPU blocklist is bypassed, so they are matched by name.
 */
function isSoftwareRenderer(gl: WebGL2RenderingContext): boolean {
  let name = String(gl.getParameter(gl.RENDERER) ?? '');
  if (!SOFTWARE_RENDERER.test(name) && /^webkit webgl$/i.test(name)) {
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    if (info) name = String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL) ?? '');
  }
  return SOFTWARE_RENDERER.test(name);
}

/** low: phones and coarse pointers; high: fine pointer with 8 GB (or 8+ cores when unknown); mid otherwise. */
export function pickTier(): NightDriveTier {
  if (isMobileViewport() || !hasFinePointer()) return 'low';
  const memory = deviceMemory();
  const strong = memory !== undefined ? memory >= 8 : (navigator.hardwareConcurrency ?? 0) >= 8;
  return strong ? 'high' : 'mid';
}
